import React, { useEffect, useState } from 'react'
import InputMask from 'react-input-mask'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  FormControl,
  HStack,
  Input,
  Text,
  VStack,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Spinner
} from '@chakra-ui/react'
import { useReferralProvider } from 'providers/referralsProvider/ReferralProvider'
import { getfetchValidateCuitWithRecaptcha } from 'services/api'
import { isInternalErrorStatus, isValidCuil } from 'utils/functions/validations'
import { IResponseReferrals } from 'services/IApi.d'
import { IErrorPreset } from './ReferralCompanyData.d'
import { ICompanyData } from 'providers/referralsProvider/ReferralProvider.d'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import FooterReferrals from 'components/footerReferrals/FooterReferrals'
import ToastComponent from '../../components/customToast/toast'

const ReferralCompanyData = () => {
  const cuilLen = 11
  const {
    register,
    getValues,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<ICompanyData>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: { cuit: '' }
  });
  const navigate = useNavigate()
  const referApprove = ["REF_ALLOWED", "REF_ALLOWED_TO_UPGRADE"]
  const referDenied = ["REF_NOT_ALLOWED_BY_SUBS_PENDING", "REF_NOT_ALLOWED", "REF_NOT_ALLOWED_BY_CUIT_ERROR"]
  const referExist = ["REF_EXISTS", "REF_NOT_ALLOWED_TO_UPGRADE", "REF_NOT_ALLOWED_BY_AUTONOMOUS_BRANCHES"]
  const { showErrorToast, closeToast } = ToastComponent()
  const [loading, setLoading] = useState<boolean>(false)
  const [isCuitAllowed, setIsCuitAllowed] = useState<boolean>(false)
  const { referrals, companyData, editCompanyData } = useReferralProvider()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const ErrorPreset: IErrorPreset = {
    invalidFormat: {
      text: 'El formato del CUIT no es válido',
    },
    invalidCuit: {
      text: 'No podemos validar el CUIT. Volvé a intentarlo.',
    },
    invalidRecaptcha: {
      text: 'No podemos continuar. Volvé a intentarlo.'
    },
    invalidReferral: {
      text: 'No podemos referir a este CUIT. Intentá con otro.'
    },
    invalidExist: {
      text: 'Este CUIT ya no puede participar del programa.'
    },
  }

  const clearCuitInput = () => {
    setValue('cuit', '')
  }

  const handleClearErrors = (event: React.FocusEvent<HTMLInputElement, Element>) => {
    const { value } = event.target
    clearErrors('cuit')
    setIsCuitAllowed(cuitValidation(value))
  };

  const onSubmit = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    setLoading(true)
    try {
      if (!executeRecaptcha) {
        console.info('Execute recaptcha not yet available')
        return
      }
      const referredData = { cuit: getValues('cuit').replace(/-/gi, '') }
      const recaptchaToken: string = await executeRecaptcha('cuitvalidate');
      if (recaptchaToken) {
        const response: IResponseReferrals = await getfetchValidateCuitWithRecaptcha(referredData.cuit, recaptchaToken)
        if (!response.data.code) {
          setError('cuit', { type: 'manual', message: ErrorPreset.invalidReferral.text })
          clearCuitInput()
          return
        } else if (referDenied.includes(response.data.code)) {
          setLoading(false)
          return setError('cuit', { type: 'manual', message: ErrorPreset.invalidReferral.text })
        }  else if (referExist.includes(response.data.code)) {
          setLoading(false)
          return setError('cuit', { type: 'manual', message: ErrorPreset.invalidExist.text })
        }else if (response.status === 401) {
          showErrorToast(ErrorPreset.invalidRecaptcha.text);
          clearCuitInput()
          return
        } else if (isInternalErrorStatus(response.status)) {
          clearCuitInput()
          return showErrorToast(ErrorPreset.invalidCuit.text)
        } else if (referApprove.includes(response.data.code)) {
          editCompanyData({ cuit: referredData.cuit, code: response.data.code })
          navigate('/suscriptor')
        } else {
          showErrorToast(ErrorPreset.invalidCuit.text)
          clearCuitInput()
          return
        }
      } else {
        showErrorToast(ErrorPreset.invalidRecaptcha.text);
        clearCuitInput()
        console.error('No reCAPTCHA token available');
      }
    } catch (error) {
      showErrorToast(ErrorPreset.invalidRecaptcha.text);
      clearCuitInput()
      console.error('Error during reCAPTCHA execution:');
    }
    setLoading(false)
  }

  function cuitValidation(data: string): boolean {
    const cuitNoDash: string = data.replace(/-/gi, '')
    if (cuitNoDash.length !== cuilLen) {
      return false
    }
    if (isValidCuil(cuitNoDash)) {
      return true
    }
    setError('cuit', { type: 'manual', message: ErrorPreset.invalidFormat.text })
    return false
  }

  useEffect(() => {
    const subscription = watch((data: ICompanyData) => {
      clearErrors("cuit")
      setIsCuitAllowed(cuitValidation(data.cuit))
    });
    return () => subscription.unsubscribe();
  }, [watch("cuit")]);


  useEffect(() => {
    setValue('cuit', companyData?.cuit ?? '')
  }, []);


  return (
    <form data-testid='cuit-form'>
      <Box maxW='400px' w='100%' margin='auto' pt='64px' height='calc(100vh - 180px)'>
        <Text as='h1' textStyle='h2Bold' textAlign='center' mb='4px'>
          Referí una empresa
        </Text>
        <Text textStyle='bodyMd' textAlign='center'>
          Completá los datos para recomendar Interbanking.
        </Text>
        <Text data-testid='referrals-text' textStyle='bodyMd' textAlign='center' color='neutral.130'>
          Pódes referir hasta {referrals} empresas por día.
        </Text>
        <Text textStyle='titleMdBold' pt='32px'>
          ¿A qué empresa querés referir?
        </Text>
        <HStack mt='16px' pb='16px' justifyContent='center'>
          <Box w='100%'>
            <VStack spacing='12'>
              <FormControl isInvalid={!!errors.cuit}>
                <FormLabel display='contents' textStyle='labelMd'>
                  CUIT
                </FormLabel>
                <Input
                  as={InputMask}
                  mask='99-99999999-9'
                  maskChar={null}
                  data-testid='cuit-input'
                  id='cuitInput'
                  placeholder='CUIT'
                  borderColor='neutral.90'
                  {...register('cuit')}
                  onFocus={handleClearErrors}
                  onKeyDown={() => closeToast()}
                  disabled={loading}
                />
                {!errors?.cuit ? (
                  <FormHelperText textStyle='labelSm' data-testid='helper-text' mt={0}>
                    Ingresá el CUIT sin guiones
                  </FormHelperText>
                ) : (
                  <FormErrorMessage textStyle='labelSm' data-testid='error-message' mt={0}>
                    {errors?.cuit?.message}
                  </FormErrorMessage>
                )}
              </FormControl>
              {loading && <Spinner variant='default' data-testid='spinner' />}
            </VStack>
          </Box>
        </HStack>
      </Box>
      <FooterReferrals
        stepNumber='1' 
        textContinue='Continuar' 
        disableContinue={loading || !isCuitAllowed} 
        onAction={onSubmit} 
        />
    </form>
  );
};

export default ReferralCompanyData
